import { Component, computed, contentChild, ViewEncapsulation } from '@angular/core';
import { TranslatePipe } from '@ngx-translate/core';

import { InputDirective } from '../input/input.directive';
import { IconComponent } from '../icon/icon.component';
import { IconButtonComponent } from '../icon-button/icon-button.component';
import type { Signal } from '@angular/core';

/**
 * This component creates input field with a button to clear the input value.
 *
 * The input itself MUST be given a width of 100%, and if sizing is needed, should be done through the parent.
 *
 * @example
 *
 * <td-wb-clear-field>
 *   <input tdWbInput [(ngModel)]="theModel" type="text">
 * </td-wb-clear-field>
 */
@Component({
  imports: [
    IconComponent,
    IconButtonComponent,
    TranslatePipe
  ],
  selector: 'td-wb-clear-field',
  templateUrl: './clear-field.component.html',
  styleUrls: ['clear-field.component.scss'],
  encapsulation: ViewEncapsulation.None,
  exportAs: 'tdWbClearField'
})
export class ClearFieldComponent {
  input: Signal<InputDirective> = contentChild.required(InputDirective);

  /**
   * Computed signal that reactively tracks whether the clear button should display.
   * Reads the input's currentValue and disabledSignal so OnPush re-renders correctly.
   */
  readonly displayClearButton: Signal<boolean> = computed(() => {
    const inputDirective = this.input();
    const value = inputDirective.currentValue();
    const disabled = inputDirective.disabledSignal();
    return !disabled && value !== null && value !== undefined && `${value}`.length > 0;
  });

  /**
   * To clear the input value and focus on the input field.
   */
  clearFieldAndFocus(): void {
    this.input().clear();
    this.input().focus();
  }
}
